import React, { useState, useReducer, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import routerConfig from './router/index';
import RouterUI from '@/router/RouterUI';
import PageLoading from '@/components/BaseComponent/PageLoading';
import AppContext from './common/AppContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import commonHelper from '@/utils/commonHelper';
import { searchPath } from '@/utils/pathMatch';
import commonSetting from '@/config/commonSetting';
import i18n from '@/i18n';
import { normalizeLanguage, setRequestLanguage, DEFAULT_LANGUAGE } from '@/i18n/language';
import Cookies from 'js-cookie';
import qs from 'qs';
import '@/utils/rumUserSync';

const hasNativeBridge = () => (
    !!(window._easybridge || window.webkit?.messageHandlers?._easybridge)
);

function reducer(prevState, action) {
    // reducer will read the value from the latest render
    const { count: prevCount } = prevState;
    const { type, payload } = action;
    if (type === 'tokens') {
        return { ...prevState, tokens: payload };
    } else if (type === 'wxInfo') {
        return { ...prevState, wxInfo: payload };
    } else if (type === 'userInfo') {
        return { ...prevState, userInfo: payload };
    } else if (type === 'tokenInfo') {
        return { ...prevState, tokenInfo: payload };
    } else if (type === 'macAddress') {
        return { ...prevState, macAddress: payload };
    } else if (type === 'diskId') {
        return { ...prevState, diskId: payload };
    } else if (type === 'version') {
        return { ...prevState, version: payload };
    } else if (type === 'clientName') {
        return { ...prevState, clientName: payload };
    } else if (type === 'language') {
        return { ...prevState, language: payload };
    } else if (type === 'deviceInfo') {
        return { ...prevState, deviceInfo: payload };
    } else {
        throw new Error();
    }
}

const App = () => {
    const [loading, setLoading] = useState(true);
    const [state, dispatch] = useReducer(reducer, {
        accountType: commonSetting.ACCOUNT_TYPE,
        productType: commonSetting.PRODUCT_TYPE,
    });
    const history = useHistory();
    const query = qs.parse(history.location.search, { ignoreQueryPrefix: true });
    const getInitialState = async () => {
        let accessToken = ''; // app会有缓存，localStorage里的token不一定是当前用户，需要从easybridge获取
        // let accessToken = commonHelper.getTokens();
        console.log('accessToken', accessToken);
        if (query?.mockToken) {
            accessToken = query?.mockToken;
            commonHelper.setTokens({ accessToken: query?.mockToken });
            dispatch({ type: 'tokens', payload: { accessToken: query?.mockToken } });
        }
        if (query.env) {
            Cookies.set('env', query.env, { domain: commonHelper.getRootDomain(window.location.hostname) });
        }

        if (history.location.pathname && !searchPath(commonSetting.WHITE_PAGE_LIST, history.location.pathname)) {
            if (!accessToken && hasNativeBridge()) {
                await commonHelper.getAppToken();
            }
        }
    };

    const getLanguageFromURL = () => {
        if (!query?.lang) return null;
        return normalizeLanguage(query.lang, null);
    };

    const setAppLanguage = (language) => {
        const lang = setRequestLanguage(language);
        console.log('set language', language, lang);
        dispatch({ type: 'language', payload: lang });
        return i18n.changeLanguage(lang).catch((e) => {
            console.error('i18n change language error', e);
        });
    };

    const resolveAppLanguage = () => {
        const urlLang = getLanguageFromURL();
        if (urlLang) {
            return Promise.resolve(setAppLanguage(urlLang));
        }
        // 网页没有原生桥：默认英文。必须写入 Context，资讯等页靠 language 才发请求
        if (!window.getLanguage || !hasNativeBridge()) {
            return Promise.resolve(setAppLanguage(DEFAULT_LANGUAGE));
        }
        return commonHelper.getAppLanguage((lang) => {
            setAppLanguage(lang || DEFAULT_LANGUAGE);
        });
    };

    useEffect(() => {
        const initializeApp = async () => {
            await Promise.all([
                getInitialState().catch((e) => {
                    console.error('getInitialState error', e);
                }),
                resolveAppLanguage().catch((e) => {
                    console.error('resolveAppLanguage error', e);
                }),
            ]);
            setLoading(false);
            if (window.getAppVersion) {
                window.getAppVersion((result) => {
                    console.log('set version', result);
                    dispatch({ type: 'version', payload: result });
                });
            }
            if (window.getDeviceInfo) {
                window.getDeviceInfo((result) => {
                    console.log('set device info', result);
                    dispatch({ type: 'deviceInfo', payload: result });
                });
            }
        };

        initializeApp();
    }, []);

    return (
      <>
        <ThemeProvider>
          <AppContext.Provider value={{ ...state, dispatch }}>
            {
                        loading ? <PageLoading />
                            : <RouterUI routers={routerConfig.routes} />
                    }
          </AppContext.Provider>
        </ThemeProvider>
      </>
    );
};

export default App;
